import { useEffect, useRef, useState } from "react"
import { useAuth } from "@/lib/auth"
import { Shell } from "@/components/Shell"
import {
  createMeal,
  deleteRecipe,
  getRecipe,
  type Label,
  type RecipeDetail,
} from "@/lib/api"
import { localToday } from "@/lib/dates"
import { LabelEditor } from "@/components/LabelEditor"
import { MethodSteps } from "@/components/MethodSteps"

/**
 * Recipe detail — the destination for /recipes/{id} taps from the library.
 *
 * Aesthetic: an editorial recipe spread. Display name as a headline,
 * eyebrow shows origin tag + parse-source + confidence, per-100g macro
 * tile beside the method summary, then the ingredients in a hairline-ruled
 * table with each row's quantity, source, and proportional kcal contribution.
 *
 * Actions: [Log full recipe] writes one meal of total_yield_g against this
 * recipe and bounces you to /today. [Delete] removes the recipe (with a
 * confirm dialog); historical meal rows that point at this recipe survive
 * because meals.recipe_id is nullable (the row goes "Unknown" rather than
 * disappearing — better than data loss).
 *
 * Out of scope (followups):
 *  - Editing ingredient quantities (currently read-only here; edit via re-parse)
 *  - "Promote to library" / "Demote to log" origin toggle
 *  - Partial-portion logging (only full yield right now)
 *  - Labels (separate PR per the queued slice list)
 */
export function RecipeDetailPage({ id }: { id: string }) {
  const auth = useAuth()
  const [recipe, setRecipe] = useState<RecipeDetail | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState<"none" | "logging" | "deleting">("none")
  const [notice, setNotice] = useState<string | null>(null)
  const busyRef = useRef(false)

  useEffect(() => {
    let cancel = false
    setRecipe(null)
    setErr(null)
    getRecipe(id)
      .then((r) => !cancel && setRecipe(r))
      .catch((e: any) => {
        if (cancel) return
        setErr(
          e?.response?.status === 404
            ? "This recipe doesn't exist (or was deleted)."
            : e?.response?.data?.error || e?.message || "Couldn't load.",
        )
      })
    return () => {
      cancel = true
    }
  }, [id])

  if (auth.status !== "authed") return null

  async function logFullRecipe(r: RecipeDetail) {
    if (busyRef.current) return
    busyRef.current = true
    setBusy("logging")
    setNotice(null)
    setErr(null)
    try {
      await createMeal({
        date: localToday(),
        meal_type: suggestMealType(),
        recipe_id: r.id,
        quantity_g: r.total_yield_g,
      })
      window.history.pushState({}, "", "/today")
      window.dispatchEvent(new PopStateEvent("popstate"))
    } catch (e: any) {
      setErr(
        e?.response?.data?.error ||
          e?.message ||
          "Couldn't log the meal. Try again?",
      )
    } finally {
      busyRef.current = false
      setBusy("none")
    }
  }

  async function remove(r: RecipeDetail) {
    if (busyRef.current) return
    if (!confirm(`Delete "${r.name}"? Past meal entries that reference it will stay logged but show as "Unknown".`)) return
    busyRef.current = true
    setBusy("deleting")
    setErr(null)
    try {
      await deleteRecipe(r.id)
      window.history.pushState({}, "", "/recipes")
      window.dispatchEvent(new PopStateEvent("popstate"))
    } catch (e: any) {
      setErr(
        e?.response?.data?.error ||
          e?.message ||
          "Couldn't delete the recipe.",
      )
      busyRef.current = false
      setBusy("none")
    }
  }

  return (
    <Shell
      currentPath="/recipes"
      navItems={[
        { href: "/", label: "Dashboard" },
        { href: "/today", label: "Today" },
        { href: "/performance", label: "Performance" },
        { href: "/ingredients", label: "Ingredients" },
        { href: "/recipes", label: "Recipes" },
        { href: "/weight", label: "Weight" },
        { href: "/profile", label: "Profile" },
      ]}
    >
      <div className="max-w-4xl mx-auto px-6 sm:px-10 py-10 sm:py-16">
        <p className="mb-6">
          <a
            href="/recipes"
            className="eyebrow text-muted-foreground hover:text-foreground transition-colors"
          >
            ← recipes
          </a>
        </p>

        {err && (
          <div className="px-4 py-3 border-l-2 border-persimmon bg-persimmon/[0.04] mb-6">
            <p className="font-sans text-sm text-persimmon">{err}</p>
          </div>
        )}

        {!recipe && !err && (
          <p className="font-mono text-xs text-muted-foreground">loading…</p>
        )}

        {recipe && <DetailBody recipe={recipe} busy={busy} notice={notice} onLog={() => logFullRecipe(recipe)} onDelete={() => remove(recipe)} />}
      </div>
    </Shell>
  )
}

function DetailBody({
  recipe: r,
  busy,
  notice,
  onLog,
  onDelete,
}: {
  recipe: RecipeDetail
  busy: "none" | "logging" | "deleting"
  notice: string | null
  onLog: () => void
  onDelete: () => void
}) {
  const totals = {
    cal: (r.per_100g.calories * r.total_yield_g) / 100,
    p: (r.per_100g.protein_g * r.total_yield_g) / 100,
    c: (r.per_100g.carbs_g * r.total_yield_g) / 100,
    f: (r.per_100g.fiber_g * r.total_yield_g) / 100,
    s: (r.per_100g.sugar_g * r.total_yield_g) / 100,
  }

  return (
    <>
      <div className="flex items-baseline gap-3 mb-3 flex-wrap animate-ink-in">
        <OriginBadge origin={r.origin} />
        {r.source_type && (
          <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            from {r.source_type}
          </span>
        )}
        {r.confidence && (
          <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            confidence · {r.confidence}
          </span>
        )}
      </div>

      <h1
        className="font-display text-display tracking-tightest leading-[1.05] mb-6 animate-fade-up"
        style={{ animationDelay: "80ms" }}
      >
        {r.name}
      </h1>

      <p
        className="font-sans text-base text-muted-foreground mb-6 max-w-2xl leading-relaxed animate-fade-up"
        style={{ animationDelay: "180ms" }}
      >
        Yields <span className="font-mono text-foreground">{r.total_yield_g}g</span>{" "}
        · saved {fmtDate(r.created_at)}
        {r.method_summary && <> · {r.ingredients.length} ingredients</>}
      </p>

      {/* Labels — editable inline. Persists via PUT /recipes/{id}/labels. */}
      <div
        className="mb-12 animate-fade-up"
        style={{ animationDelay: "220ms" }}
      >
        <LabelEditor
          recipeId={r.id}
          initial={r.labels}
          onChange={(next: Label[]) => {
            // Sync local recipe object so any sibling rendering (none today,
            // but future hooks) sees the new label set.
            r.labels = next
          }}
        />
      </div>

      {/* Two-column on desktop: macros block + method/yield. Stacked on mobile. */}
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 mb-14">
        <section className="animate-fade-up" style={{ animationDelay: "240ms" }}>
          <p className="eyebrow mb-4">macros · per 100g</p>
          <div className="card-surface px-6 sm:px-8 py-7">
            <div className="space-y-4">
              <MacroLine label="calories" value={r.per_100g.calories} unit="kcal" emphasized />
              <Divider />
              <MacroLine label="protein" value={r.per_100g.protein_g} unit="g" />
              <MacroLine label="carbs" value={r.per_100g.carbs_g} unit="g" />
              <MacroLine label="fiber" value={r.per_100g.fiber_g} unit="g" />
              <MacroLine label="sugar" value={r.per_100g.sugar_g} unit="g" tone="warn" />
            </div>
            <div className="rule my-6" />
            <p className="eyebrow mb-3">whole recipe · {r.total_yield_g}g</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 font-mono text-sm tabular-nums">
              <span className="text-muted-foreground">calories</span>
              <span className="text-right">{totals.cal.toFixed(0)} kcal</span>
              <span className="text-muted-foreground">protein</span>
              <span className="text-right">{totals.p.toFixed(0)} g</span>
              <span className="text-muted-foreground">carbs</span>
              <span className="text-right">{totals.c.toFixed(0)} g</span>
              <span className="text-muted-foreground">fiber</span>
              <span className="text-right">{totals.f.toFixed(0)} g</span>
              <span className="text-muted-foreground">sugar</span>
              <span className="text-right">{totals.s.toFixed(0)} g</span>
            </div>
          </div>
        </section>

        <section className="animate-fade-up" style={{ animationDelay: "320ms" }}>
          {r.method_summary && (
            <div className="mb-10">
              <p className="eyebrow mb-4">method</p>
              <MethodSteps text={r.method_summary} />
            </div>
          )}
          <p className="eyebrow mb-4">actions</p>
          {notice && (
            <p className="font-mono text-xs text-moss mb-3">{notice}</p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onLog}
              disabled={busy !== "none"}
              className="bg-foreground text-background px-6 py-3 rounded font-sans text-sm tracking-tight hover:bg-moss transition-colors disabled:opacity-50"
              title={`Logs ${r.total_yield_g}g as a single meal entry on today's log.`}
            >
              {busy === "logging" ? "Logging…" : "Log full recipe →"}
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={busy !== "none"}
              className="eyebrow text-muted-foreground hover:text-persimmon transition-colors disabled:opacity-50"
            >
              {busy === "deleting" ? "deleting…" : "delete"}
            </button>
          </div>
          <p className="font-mono text-[11px] text-muted-foreground mt-3 leading-relaxed">
            Need a partial portion? Use the <a href="/today" className="underline decoration-moss/40 underline-offset-2 hover:text-foreground">Today</a>{" "}
            <span className="italic">Pick item</span> picker — it'll surface
            this recipe by name and let you set grams.
          </p>
        </section>
      </div>

      {/* Ingredients */}
      <section
        className="animate-fade-up"
        style={{ animationDelay: "400ms" }}
      >
        <div className="flex items-baseline justify-between mb-4">
          <p className="eyebrow">
            ingredients · {r.ingredients.length}
          </p>
          <p className="font-mono text-[11px] text-muted-foreground tabular-nums">
            total {r.ingredients.reduce((a, i) => a + i.quantity_g, 0).toFixed(0)}g
          </p>
        </div>
        <ul className="border-t border-b border-border divide-y divide-border">
          {r.ingredients.map((ing, idx) => {
            const ingCal = (ing.calories_per_100g * ing.quantity_g) / 100
            const ingProt = (ing.protein_per_100g * ing.quantity_g) / 100
            return (
              <li
                key={ing.id + "-" + idx}
                className="py-4 grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_auto_auto] items-baseline gap-x-4 gap-y-1"
              >
                <div className="min-w-0">
                  <p className="font-sans text-base text-foreground">
                    {ing.name}
                    {ing.state && (
                      <span className="font-sans text-sm text-muted-foreground italic">
                        {" · "}
                        {ing.state}
                      </span>
                    )}
                  </p>
                  <p className="font-mono text-[11px] text-muted-foreground tabular-nums">
                    {sourceBadge(ing.source)} ·{" "}
                    {ing.calories_per_100g.toFixed(0)} kcal/100g
                  </p>
                </div>
                <p className="font-mono text-sm text-foreground tabular-nums whitespace-nowrap">
                  {ing.quantity_g.toFixed(0)}
                  <span className="text-[10px] text-muted-foreground ml-0.5">g</span>
                </p>
                <p className="hidden sm:block font-mono text-sm text-muted-foreground tabular-nums whitespace-nowrap min-w-[8rem] text-right">
                  {ingCal.toFixed(0)} kcal · {ingProt.toFixed(1)}g p
                </p>
              </li>
            )
          })}
        </ul>
      </section>
    </>
  )
}

// ---- primitives ----

function OriginBadge({ origin }: { origin: "saved" | "log" }) {
  if (origin === "log") {
    return (
      <span
        className="font-mono text-[10px] uppercase tracking-wider text-moss border border-moss/40 rounded px-1.5 py-0.5"
        title="Auto-saved by the meal composer (origin: log)"
      >
        log
      </span>
    )
  }
  return (
    <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded px-1.5 py-0.5">
      saved
    </span>
  )
}

function MacroLine({
  label,
  value,
  unit,
  tone = "default",
  emphasized = false,
}: {
  label: string
  value: number
  unit: string
  tone?: "default" | "warn"
  emphasized?: boolean
}) {
  const color = tone === "warn" ? "text-persimmon" : "text-foreground"
  const size = emphasized ? "text-5xl" : "text-2xl"
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="eyebrow">{label}</span>
      <div className="flex items-baseline gap-1.5">
        <span className={`font-display ${size} ${color} num tracking-tightest`}>
          {value.toFixed(label === "calories" ? 0 : 1)}
        </span>
        <span className="font-mono text-[10px] text-muted-foreground">
          {unit}
        </span>
      </div>
    </div>
  )
}

function Divider() {
  return <div className="h-px w-full bg-border" />
}

function sourceBadge(source: "usda" | "claude" | "user" | "recipe"): string {
  if (source === "usda") return "USDA"
  if (source === "claude") return "estimated"
  if (source === "user") return "your library"
  return "recipe"
}

function suggestMealType(): "breakfast" | "lunch" | "dinner" | "snack" {
  const h = new Date().getHours()
  if (h < 10) return "breakfast"
  if (h < 14) return "lunch"
  if (h < 21) return "dinner"
  return "snack"
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year:
        d.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
    })
  } catch {
    return iso
  }
}
